import { useCallback } from "react";

import { useQueryClient } from "@tanstack/react-query";
import noop from "lodash/noop";

import { BasicModal } from "@similarweb/ui-components";

import { useTopicsContext } from "app/pages/activation/context/Topics/TopicsContext";
import { useTopicsDataContext } from "app/pages/activation/context/Topics/TopicsDataContext";
import { useTopicsModalContext } from "app/pages/activation/context/Topics/TopicsModalContext";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { translate } from "app/filters/i18nFilter";
import { getModalConfig } from "app/pages/activation/components/Topics/Modal/constants";
import { ModalFlow } from "app/pages/activation/components/Topics/Modal/types";
import { ACTIVATION_OBJECT_QUERY_KEY } from "app/pages/activation/queries/data/useActivationObjectQuery";
import { useGetTopicQuery } from "app/pages/activation/queries/topics/useGetTopicQuery";
import { TOPICS_QUERY_KEY } from "app/pages/activation/queries/topics/useGetTopicsDataQuery";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { ActivationObjectSchema } from "app/pages/activation/types/api";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";

import { FullScreenLoader } from "../../Shared/FullScreenLoader/FullScreenLoader";
import { DELETE_TOPICS_WITH_KW_TOAST, DELETE_TOPICS_WITHOUT_TOAST } from "../texts";

import { Flow } from "./Flow/Flow";

export const Modal = () => {
    const { showSuccessToast } = useToasts();

    const { modalData, onDeleteMenuClick, onClose, isOpen } = useTopicsModalContext();

    const { activationId, granularity, deleteTopic, updateTopic } = useTopicsContext();
    const { createdTopics } = useTopicsDataContext();

    const {
        data: selectedTopic,
        isLoading,
        isFetching,
    } = useGetTopicQuery(activationId, modalData?.data?.topicId);

    const queryClient = useQueryClient();

    const keywordsLimit = ActivationComponentAssets.maxAmountOfKeywords;

    const onDelete = useCallback(
        (topicId: string | IUserKeywordGroup, shouldRemoveKWList = true) => {
            const id = typeof topicId === "string" ? topicId : topicId.Id;

            deleteTopic(
                { activationId, topicsId: [id], shouldRemoveKWList },
                {
                    onSuccess: () => {
                        let removedTopicName = "";

                        const track = activationTracking.home;

                        const topicsAfterDelete = createdTopics.filter(({ topicId, topicName }) => {
                            if (topicId === id) {
                                removedTopicName = topicName;
                            }

                            return topicId !== id;
                        });
                        const activationObject: ActivationObjectSchema = queryClient.getQueryData([
                            ACTIVATION_OBJECT_QUERY_KEY,
                            activationId,
                        ]);
                        const activationTopicsAfterDelete = activationObject.assets?.topics.filter(
                            (topicId) => id !== topicId,
                        );
                        const activationAfterDelete = {
                            ...activationObject,
                            assets: { ...activationObject.assets, topics: activationTopicsAfterDelete },
                        };

                        queryClient.setQueryData([TOPICS_QUERY_KEY, granularity, activationId], topicsAfterDelete);
                        queryClient.setQueryData([ACTIVATION_OBJECT_QUERY_KEY, activationId], activationAfterDelete);
                        queryClient.invalidateQueries({ queryKey: [TOPICS_QUERY_KEY, granularity, activationId] });

                        track.trackTopicDelete(shouldRemoveKWList);
                        onClose();

                        if (shouldRemoveKWList) {
                            showSuccessToast(translate(DELETE_TOPICS_WITH_KW_TOAST, { name: removedTopicName }));

                            return;
                        }

                        showSuccessToast(translate(DELETE_TOPICS_WITHOUT_TOAST, { name: removedTopicName }));
                    },
                },
            );
        },
        [activationId, createdTopics, deleteTopic, granularity, onClose, queryClient],
    );

    const onEdit = (topic: IUserKeywordGroup) => {
        const { Id, Keywords, Name } = topic;

        updateTopic(
            {
                activationId,
                topic: {
                    id: Id,
                    keywords: Keywords,
                    name: Name,
                },
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: [TOPICS_QUERY_KEY, granularity, activationId] });
                    onClose();
                },
            },
        );
    };

    const onDeleteFromEdit = () => {
        const isShared = modalData?.data?.isShared;
        const modalFlow = isShared ? ModalFlow.DeleteShared : ModalFlow.Delete;

        onDeleteMenuClick(selectedTopic.id, isShared, modalFlow);
    };

    if (modalData?.modalFlow === ModalFlow.Edit) {
        if (isLoading || isFetching) {
            return <FullScreenLoader />;
        }

        if (!selectedTopic) {
            return null;
        }

        const group = {
            Id: selectedTopic.id,
            GroupHash: selectedTopic.id,
            Keywords: selectedTopic.keywords,
            Name: selectedTopic.name,
            AddedTime: "",
            LastUpdated: "",
            UserId: "",
            sharedWithAccounts: [],
            sharedWithUsers: [],
        };

        return (
            <KeywordsGroupEditorModal
                onClose={onClose}
                open={isOpen}
                keywordsGroup={group}
                seedKeyword={selectedTopic.name}
                showDeleteButton={false}
                maxKeywords={keywordsLimit}
                onEdit={onEdit}
                onBeforeDelete={onDeleteFromEdit}
                isEditToastWithLink={false}
                ignoreDuplicateCheck
            />
        );
    }

    return (
        <BasicModal
            isOpen={isOpen}
            onRequestClose={onClose}
            customStyles={getModalConfig(modalData?.modalFlow)}
            closeIconConfig={{
                show: false,
                onClick: noop,
            }}
        >
            <Flow
                topicId={modalData?.data?.topicId}
                flow={modalData?.modalFlow}
                isShared={modalData?.data?.isShared}
                onDelete={onDelete}
            />
        </BasicModal>
    );
};

Modal.displayName = "TopicModal";
